/* eslint-disable no-nested-ternary */
import { ReactComponent as ClearIcon } from "@icons/close-circle.svg";
import { ReactComponent as PopupIcon } from "@icons/down-arrow.svg";
import { ReactComponent as ErrorInfoIcon } from "@icons/error-info.svg";
import { FormHelperText, IconButton, InputLabel } from "@mui/material";
import { useEffect, useState } from "react";

import Colors from "@constants/Colors";
import AlertTriangle from "@icons/AlertTriangle";
import {
  StyledDropdown,
  StyledFormControl,
  StyledMenuItem,
  StyledTypography,
} from "./styles";

interface SelectDropdownFieldProps {
  labelKey: string;
  value: string;
  label: string;
  disabled: boolean;
  options: string[];
  handleChange: (key: string, value: string) => void;
  nodata: boolean;
}

const SelectDropdownField = ({
  labelKey,
  value,
  label,
  disabled,
  options,
  handleChange,
  nodata,
}: SelectDropdownFieldProps) => {
  const [error, setError] = useState("");
  const handleClose = () => {
    handleChange(labelKey, "");
    if (labelKey === "property") {
      return setError(`Please select a ${labelKey} to continue.`);
    }
    return setError("");
  };

  useEffect(() => {
    if (nodata) {
      setError("Error! No data available");
    }
  }, [nodata]);

  return (
    <StyledFormControl
      disabled={disabled}
      fullWidth
      error={error !== "" || nodata}
      nodata={nodata ? "true" : ""}
    >
      <InputLabel shrink id={label}>
        {label}
      </InputLabel>
      <StyledDropdown
        MenuProps={{
          MenuListProps: {
            disablePadding: true,
          },
          PaperProps: {
            style: {
              marginTop: "10px",
              border: `1px solid ${Colors.neutral_gray_300}`,
              padding: "0px !important",
              borderRadius: "8px",
              boxShadow: Colors.Elevation_4,
            },
          },
        }}
        labelId={label}
        id={value}
        value={value}
        disabled={nodata || disabled}
        label={label}
        notched
        IconComponent={PopupIcon}
        fullWidth
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderValue={(selected: any) => {
          if (selected?.length === 0) {
            return (
              <StyledTypography
                key="select"
                disabled={disabled}
                error={error !== "" || nodata || undefined}
              >
                Select
              </StyledTypography>
            );
          }
          if (nodata) {
            return (
              <StyledTypography
                key="select"
                disabled={disabled}
                error={error !== "" || nodata || undefined}
              >
                Select
              </StyledTypography>
            );
          }

          return selected;
        }}
        onChange={(event) => {
          handleChange(labelKey, event.target.value as string);
          setError("");
        }}
        displayEmpty
        sx={{
          "& .MuiSelect-iconOutlined": {
            display: value !== "" || value || error !== "" ? "none" : "",
          },
          "&.Mui-focused .MuiIconButton-root": { color: "primary.main" },
        }}
        endAdornment={
          (value !== "" || error !== "") &&
          (!nodata ? (
            <IconButton onClick={handleClose}>
              {error !== "" && value === "" ? (
                <ErrorInfoIcon />
              ) : disabled ? null : (
                <ClearIcon />
              )}
            </IconButton>
          ) : (
            <IconButton>
              <AlertTriangle
                color={Colors.error_500}
                width="24px"
                height="24px"
                strokeWidth="1.2px"
              />
            </IconButton>
          ))
        }
      >
        {options?.length > 0 &&
          options?.map((option) => (
            <StyledMenuItem
              sx={{
                "&.Mui-selected": {
                  backgroundColor: Colors.neutral_gray_100,
                  color: Colors.primary,
                },
                "&:focus": {
                  backgroundColor: `${Colors.neutral_gray_100} !important`,
                },
              }}
              key={option}
              value={option}
            >
              {option}
            </StyledMenuItem>
          ))}
      </StyledDropdown>
      <FormHelperText>{(error !== "" || nodata) && error}</FormHelperText>
    </StyledFormControl>
  );
};

export default SelectDropdownField;
